/**
 * Ziyaret Listesi — ZIYARET_TAKIP.md §5.2. KATMAN 2.
 *
 * Her satır bir ziyaret: giriş, çıkış, süre, sonuç, satmama nedeni, GPS, plan sapma.
 * Satıra tıklanınca sağdan detay drawer'ı açılır ve Katman 3 hareket timeline'ı görünür.
 *
 * SÜRE VURGUSU (§5.2): kısa eşiğin altı amber, çok kısa eşiğin altı kırmızı — eşikler
 * SUNUCUDAN gelir (Setting'ten okunabilir), burada sabitlenmez.
 */

import { useMemo, useState } from 'react';
import { fmtDuration, fmtMoney, fmtNum, fmtTime, OUTCOME_LABELS, OUTCOME_PILL } from './shared';
import { IcExcel, IcTrash } from './icons';

const QUICK_FILTERS = [
  { key: '', label: 'Tümü' },
  { key: 'success', label: 'Başarılı' },
  { key: 'no_sale', label: 'Satış Yok' },
  { key: 'offRoute', label: 'Rut Dışı' },
  { key: 'short', label: 'Kısa ziyaret' },
  { key: 'gps', label: 'GPS mesafe aşımı' },
];

export function VisitListTab({ data, loading, contextLabel, onOpenVisit, onDeleteVisit, onExcel, canDelete }) {
  const [quick, setQuick] = useState('');

  // Stabil referans: `data?.items || []` her render yeni dizi üretir ve aşağıdaki
  // useMemo'yu her seferinde yeniden çalıştırırdı.
  const items = useMemo(() => data?.items || [], [data]);
  const counts = data?.counts;
  const th = data?.thresholds || {};

  const rows = useMemo(() => {
    switch (quick) {
      case 'success': return items.filter((v) => v.isSuccessful === true);
      case 'no_sale': return items.filter((v) => v.outcome === 'no_sale');
      case 'offRoute': return items.filter((v) => v.isInRoute === false);
      case 'short': return items.filter((v) => v.durationSec != null && v.durationSec < (th.shortVisitSec ?? 300));
      case 'gps': return items.filter((v) => v.gpsWithinDistance === false);
      default: return items;
    }
  }, [items, quick, th.shortVisitSec]);

  return (
    <div className="pane on">
      {contextLabel && (
        <div className="alert info m" style={{ marginBottom: 12 }}><div>{contextLabel}</div></div>
      )}

      <div className="stat c6" style={{ marginBottom: 14 }}>
        <Mini label="Ziyaret" value={fmtNum(counts?.total)} />
        <Mini cls="c-green" label="Başarılı" value={fmtNum(counts?.success)} />
        <Mini cls="c-red" label="Satış Yok" value={fmtNum(counts?.noSale)} />
        <Mini cls="c-purple" label="Rut Dışı" value={fmtNum(counts?.offRoute)} />
        <Mini cls="c-amber" label="Kısa Ziyaret" value={fmtNum(counts?.shortVisit)} />
        <Mini cls="c-red" label="GPS Aşımı" value={fmtNum(counts?.outOfRadius)} />
      </div>

      <div className="card">
        <div className="sec-h" style={{ padding: '14px 16px 0', marginBottom: 10 }}>
          <div>
            <b>Ziyaretler</b>
            <span className="sub" style={{ display: 'block' }}>
              Her satır bir ziyaret — giriş, çıkış, süre. Satıra tıklayınca hareket detayı açılır.
            </span>
          </div>
          <div style={{ flex: 1 }} />
          <button type="button" className="btn sec sm" onClick={onExcel}><IcExcel size={15} />Excel</button>
        </div>

        <div className="quickbar">
          {QUICK_FILTERS.map((f) => (
            <span key={f.key} className={`chip ${quick === f.key ? 'on' : ''}`}
              onClick={() => setQuick(f.key)} role="button" tabIndex={0}
              onKeyDown={(e) => e.key === 'Enter' && setQuick(f.key)}>
              {f.label}
              <span className="cnt">{countFor(f.key, counts, items.length)}</span>
            </span>
          ))}
        </div>

        <div className="dg-wrap">
          <table className="dg">
            <thead>
              <tr>
                <th className="r">#</th><th>Müşteri Kodu</th><th>Müşteri</th><th>Rut</th>
                <th>Giriş</th><th>Çıkış</th><th className="r">Süre</th>
                <th>Sonuç</th><th>Satmama Nedeni</th><th className="r">Hareket</th>
                <th className="r">Sipariş ₺</th><th className="r">Tahsilat ₺</th><th className="r">Çeşit</th>
                <th>GPS</th><th className="r">Plan Sapma</th>
                {canDelete && <th className="act-th" />}
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={canDelete ? 16 : 15} style={{ textAlign: 'center', padding: 24 }}>Yükleniyor…</td></tr>
              )}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={canDelete ? 16 : 15} style={{ textAlign: 'center', padding: 24, color: 'var(--t3)' }}>
                  Ziyaret kaydı yok.
                </td></tr>
              )}
              {rows.map((v) => (
                <tr key={v.id} className="clk" onClick={() => onOpenVisit(v)}>
                  <td className="r tnum">{v.sequenceNo ?? '—'}</td>
                  <td className="tnum">{v.customerCode || '—'}</td>
                  <td>{v.customerTitle || '—'}</td>
                  <td>
                    {v.isInRoute === null || v.isInRoute === undefined
                      ? <span className="sub">—</span>
                      : <span className={`pill soft ${v.isInRoute ? 'gr' : 'a'}`}>
                        {v.isInRoute ? 'Rut İçi' : 'Rut Dışı'}
                      </span>}
                  </td>
                  <td className="tnum">{fmtTime(v.startedAt, true)}</td>
                  <td className="tnum">{fmtTime(v.endedAt, true)}</td>
                  <td className="r tnum" style={durationStyle(v.durationSec, th)}>{fmtDuration(v.durationSec)}</td>
                  <td>
                    {v.outcome
                      ? <span className={`pill ${OUTCOME_PILL[v.outcome] || 'gr'}`}>
                        {OUTCOME_LABELS[v.outcome] || v.outcome}
                      </span>
                      : <span className="sub">—</span>}
                  </td>
                  <td>{v.nonSaleReasonName || '—'}</td>
                  <td className="r tnum">{fmtNum(v.activityCount)}</td>
                  <td className="r tnum">{fmtMoney(v.orderAmount)}</td>
                  <td className="r tnum">{fmtMoney(v.collectionAmount)}</td>
                  <td className="r tnum">{fmtNum(v.skuCount)}</td>
                  <td>
                    {v.gpsWithinDistance === true && (
                      <span className="pill soft g">{v.gpsDistanceMeters ?? '?'} m</span>
                    )}
                    {v.gpsWithinDistance === false && (
                      <span className="pill soft r">Aşım · {v.gpsDistanceMeters ?? '?'} m</span>
                    )}
                    {v.gpsWithinDistance === null || v.gpsWithinDistance === undefined
                      ? <span className="sub">—</span> : null}
                  </td>
                  <td className="r tnum" style={v.isTimeViolation ? { color: 'var(--m-amber)', fontWeight: 700 } : undefined}>
                    {v.timeViolationMin != null
                      ? `${v.timeViolationMin > 0 ? '+' : ''}${v.timeViolationMin} dk`
                      : '—'}
                  </td>
                  {canDelete && (
                    <td className="actcell">
                      <button type="button" className="ra del" title="Ziyareti sil (nedeni sorulur)"
                        onClick={(e) => { e.stopPropagation(); onDeleteVisit(v); }}>
                        <IcTrash size={14} />
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/**
 * §5.2 süre vurgusu. Eşikler SUNUCUDAN gelir; burada sabit sayı YOK ki
 * Setting'ten değiştirilen eşik ekranda da geçerli olsun.
 */
function durationStyle(sec, th) {
  if (sec == null) return undefined;
  if (sec < (th.veryShortVisitSec ?? 120)) return { color: 'var(--m-red)', fontWeight: 700 };
  if (sec < (th.shortVisitSec ?? 300)) return { color: 'var(--m-amber)', fontWeight: 700 };
  return undefined;
}

function countFor(key, counts, total) {
  if (!counts) return 0;
  switch (key) {
    case 'success': return counts.success;
    case 'no_sale': return counts.noSale;
    case 'offRoute': return counts.offRoute;
    case 'short': return counts.shortVisit;
    case 'gps': return counts.outOfRadius;
    default: return counts.total ?? total;
  }
}

function Mini({ cls = '', label, value }) {
  return (
    <div className={`scard ${cls}`}>
      <div className="l">{label}</div>
      <div className="v">{value}</div>
    </div>
  );
}
